import React, { FC, Fragment, ReactNode } from 'react';
import { UnderlineTextButton } from '@components/UnderlineTextButton';
import { Divider, Typography } from '@mui/material';
import {
  activeCategoryCount,
  isCiaCategory,
  ModelFilterCategory,
  ModelFilterState,
  useModelFilterActions,
  useModelFilters,
} from '@store/zustand/model-filters';
import {
  CATEGORY_GROUPS,
  CATEGORY_LABELS,
} from '@views/Project/components/ModelFiltersPopover/categories';
import FilterCategoryRow from '@views/Project/components/ModelFiltersPopover/components/FilterCategoryRow';
import FilterPanelLayout from '@views/Project/components/ModelFiltersPopover/components/FilterPanelLayout';

type Props = {
  onOpenCategory: (category: ModelFilterCategory) => void;
  availability: Partial<Record<ModelFilterCategory, boolean>>;
  labels: Partial<Record<ModelFilterCategory, Record<string, string>>>;
  icons: Partial<Record<ModelFilterCategory, ReactNode>>;
};

/**
 * The values a category row summarises. A CIA category's selection is spread
 * over its criteria, so the row sees every criteria's answers as one list.
 */
const selectionFor = (
  state: ModelFilterState,
  category: ModelFilterCategory,
): string[] =>
  isCiaCategory(category)
    ? Object.values(state.ciaFilters[category]).flat()
    : state[category];

/**
 * The Filters root: a header and the AC3.1 category list, grouped by
 * dividers.
 *
 * It renders no values of its own. A row click hands the category upwards and
 * the container swaps this panel for the category panel in the same popover,
 * which is what AC4.1.1 means by "no second popover". A category whose
 * `availability` is `false` is left out together with its divider when it
 * was the last of its group (AC3.1.1 to AC3.1.3); the data behind that
 * decision is resolved by the container, so this panel fetches nothing.
 * "Clear all" appears only while some category carries a selection (AC2.2)
 * and empties every category at once. The Share filters pill AC2.2 places
 * beside the title belongs to QSP-3575 and is not rendered until it does
 * something.
 */
const FiltersRootPanel: FC<Props> = ({
  onOpenCategory,
  availability,
  labels,
  icons,
}) => {
  const state = useModelFilters();
  const { clearAll } = useModelFilterActions();

  const visibleGroups = CATEGORY_GROUPS.map((group) =>
    group.filter((category) => availability[category] !== false),
  ).filter((group) => group.length > 0);

  return (
    <FilterPanelLayout
      title={
        <Typography variant="h5" color="grey.1000">
          Filters
        </Typography>
      }
      action={
        activeCategoryCount(state) > 0 && (
          <UnderlineTextButton
            $disabled={false}
            onClick={clearAll}
            sx={{ flexShrink: 0 }}
          >
            Clear all
          </UnderlineTextButton>
        )
      }
    >
      {visibleGroups.map((group, index) => (
        <Fragment key={group.join('-')}>
          {index > 0 && <Divider sx={{ borderColor: 'grey.500' }} />}

          {group.map((category) => (
            <FilterCategoryRow
              key={category}
              icon={icons[category]}
              label={CATEGORY_LABELS[category]}
              values={selectionFor(state, category)}
              labels={labels[category] ?? {}}
              onClick={() => onOpenCategory(category)}
            />
          ))}
        </Fragment>
      ))}
    </FilterPanelLayout>
  );
};

export default FiltersRootPanel;
